import { FC, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Popover } from '@mui/material';
import dayjs from 'dayjs';

import { Text } from '../../../shared/uiComponents';
import { ORANGE, TEXT as TEXT_COLORS } from '../../../shared/Helpers/colors';

import { DispatchProperties, useSelector } from '../../../redux/store';
import { getPendingRequests } from '../../../redux/State/clientSlice/scheduleCancellationSlice';
import { AdminTypes } from '../../../components/Menu';
import { CancellingParty, RescheduleIntent } from '../../../redux/API/ClientAPIHelpers/scheduleCancellation';

const cancellingPartyLabels: Record<CancellingParty, string> = {
	[CancellingParty.Provider]: 'BT (Provider)',
	[CancellingParty.ClientFamily]: 'Client/Family',
	[CancellingParty.Agency]: 'Agency',
};

const rescheduleIntentLabels: Record<RescheduleIntent, string> = {
	[RescheduleIntent.Yes]: 'Yes - will coordinate a make-up session',
	[RescheduleIntent.No]: 'No - this session will not be made up',
	[RescheduleIntent.NotSure]: 'Not sure yet',
};

const PendingRequestsChip: FC = () => {
	const dispatch = useDispatch<DispatchProperties>();
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

	const role = useSelector((state) => state.account.role);
	const pendingRequests = useSelector((state) => state.scheduleCancellation.pendingRequests);
	const filteredTherapist = useSelector((state) => state.calendarHelper.filterData.therapist);
	const isAdmin = role?.section.id === AdminTypes.admin;

	useEffect(() => {
		if (isAdmin) {
			dispatch(getPendingRequests(undefined));
		}
	}, [dispatch, isAdmin]);

	const count = pendingRequests.length;

	if (!isAdmin || count === 0 || filteredTherapist?.id) return null;

	return (
		<>
			<div
				onClick={(e) => setAnchorEl(e.currentTarget)}
				style={{
					display: 'inline-flex',
					alignItems: 'center',
					backgroundColor: ORANGE.QUATERNARY,
					color: ORANGE.DARK[16],
					border: `1px solid ${ORANGE.LIGHT[48]}`,
					borderRadius: '4px',
					padding: '4px 10px',
					fontSize: '12px',
					fontWeight: 600,
					lineHeight: '16px',
					cursor: 'pointer',
				}}
			>
				{`${count} request${count === 1 ? '' : 's'} pending`}
			</div>
			<Popover
				open={Boolean(anchorEl)}
				anchorEl={anchorEl}
				onClose={() => setAnchorEl(null)}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
				transformOrigin={{ vertical: 'top', horizontal: 'left' }}
				slotProps={{
					paper: {
						sx: {
							borderRadius: '12px',
							padding: '16px',
							maxWidth: '340px',
							boxShadow: '0px 8px 24px rgba(0,0,0,0.12)',
						},
					},
				}}
			>
				<div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
					<Text
						title={`${count} Pending Request${count === 1 ? '' : 's'}`}
						type="psm"
						fontWeight="semiBold"
					/>
					{pendingRequests.slice(0, 5).map((req) => (
						<div key={req.groupId} style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
							<Text title={req.providerName} type="pxs" fontWeight="semiBold" />
							<Text
								title={`${dayjs(req.startDate).format('MMM D')} \u2013 ${dayjs(req.endDate).format('MMM D')} \u2022 ${req.reasonName || 'Unknown'}`}
								type="pxs"
								color={TEXT_COLORS.TERTIARY}
							/>
							<Text
								title={`Cancelling party: ${req.cancellingParty !== null ? cancellingPartyLabels[req.cancellingParty] : 'Unknown'}`}
								type="pxs"
								color={TEXT_COLORS.TERTIARY}
							/>
							<Text
								title={`Reschedule intent: ${req.rescheduleIntent !== null ? rescheduleIntentLabels[req.rescheduleIntent] : 'Not specified'}`}
								type="pxs"
								color={TEXT_COLORS.TERTIARY}
							/>
							{req.notes && (
								<Text title={`Notes: "${req.notes}"`} type="pxs" color={TEXT_COLORS.TERTIARY} />
							)}
						</div>
					))}
					{count > 5 && (
						<Text
							title={`+ ${count - 5} more`}
							type="pxs"
							color={TEXT_COLORS.TERTIARY}
						/>
					)}
				</div>
			</Popover>
		</>
	);
};

export default PendingRequestsChip;
